import type { IUser } from '@condo/domain';
import { FlagProvider, UnleashClient, useUnleashContext } from '@unleash/proxy-client-react';
import type { User as FirebaseUser } from 'firebase/auth';
import { useEffect, useMemo } from 'react';
import { useAuth } from '@/shared/components/auth/AuthProvider';
import { isAuthenticatedRamConfig } from '@/shared/env/runtime-config';
import type { IRamConfig } from '@/shared/env/types';
import { buildRamUnleashContext } from '@/shared/feature-flags/unleash-context';
import { useConfig } from '@/shared/utils/config/config-context';
import { useUsers } from '../model/UsersContext';

/**
 * Unleash provider wrapper that conditionally initializes the Unleash client.
 * Only mounts FlagProvider when UNLEASH_API_TOKEN and UNLEASH_API_URL are configured.
 *
 * Matches Cockpit's registerUnleashPlugin + MainApp.vue context sync behavior.
 *
 * Source:
 * - services/core/cockpit/src/bootstrap/index.ts (registerUnleashPlugin)
 * - services/core/cockpit/src/root-containers/MainApp.vue (context sync)
 */
export function UnleashProvider({ children }: { children: React.ReactNode }) {
    const { config } = useConfig();
    const { user: firebaseUser } = useAuth();
    const { getUser } = useUsers();
    const authenticatedConfig = isAuthenticatedRamConfig(config) ? config : null;

    // Get domain user from firebase uid
    const domainUser = firebaseUser ? getUser(firebaseUser.uid) : null;

    const client = useMemo(() => {
        if (!authenticatedConfig?.UNLEASH_API_TOKEN || !authenticatedConfig?.UNLEASH_API_URL) {
            return null;
        }

        return new UnleashClient({
            url: authenticatedConfig.UNLEASH_API_URL,
            clientKey: authenticatedConfig.UNLEASH_API_TOKEN,
            refreshInterval: 15,
            appName: authenticatedConfig.SERVICE_NAME,
        });
    }, [authenticatedConfig]);

    if (!client || !authenticatedConfig) {
        // No Unleash configured locally — hooks resolve config fallback values without mounting an SDK client.
        return <>{children}</>;
    }

    return (
        <FlagProvider unleashClient={client}>
            <UnleashContextSync config={authenticatedConfig} firebaseUser={firebaseUser} domainUser={domainUser} />
            {children}
        </FlagProvider>
    );
}

/**
 * Internal component that syncs Unleash context when auth/user state changes.
 * Must be inside FlagProvider to use useUnleashContext().
 */
function UnleashContextSync({ config, firebaseUser, domainUser }: { config: IRamConfig; firebaseUser: FirebaseUser | null; domainUser: IUser | null }) {
    const updateContext = useUnleashContext();

    useEffect(() => {
        if (updateContext && config) {
            const context = buildRamUnleashContext(config, firebaseUser, domainUser);
            updateContext(context);
        }
    }, [config, firebaseUser, domainUser, updateContext]);

    return null;
}
